import { Component, OnInit } from "@angular/core";
import { DynamicDialogRef, DialogService, DynamicDialogConfig } from "primeng/dynamicdialog";
import { firstValueFrom } from "rxjs";
import {
  ItemDetail,
  ShipBin,
} from "app/model/entities/entity-model";
import { AgFns, GridOptionsExt } from "app/shared/ag-fns";
import { AgStyleFns } from "app/shared/ag-style-fns";
import { NavFns } from "app/shared/nav-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { DbSaveService } from "app/shared/db-save.service";
import { ToastrService } from "ngx-toastr";
import { Router } from "@angular/router";

export interface ShipBinAddonDialogParams {
  shipBin: ShipBin;
}

@Component({
  selector: "app-ship-bin-addon-dialog",
  templateUrl: "./ship-bin-addon-dialog.component.html",
  standalone: false,
})
export class ShipBinAddonDialogComponent implements OnInit {
  isPageReady = false;

  shipBin!: ShipBin;
  addonItems: ItemDetail[] = [];

  addonGridOptions!: GridOptionsExt;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig<ShipBinAddonDialogParams>,
    private dbQueryService: DbQueryService,
    private dbSaveService: DbSaveService,
    private toastr: ToastrService,
    private router: Router,
  ) {}

  public static async openDialog(dialogService: DialogService, data: ShipBinAddonDialogParams) {
    const ref = dialogService.open(ShipBinAddonDialogComponent, {
      header: "In-Process Add-On Items",
      closable: true,
      draggable: true,
      modal: true,
      width: "75vw",
      height: "65vh",
      focusOnShow: false,
      data: data,
    });
    return await firstValueFrom(ref.onClose);
  }

  async ngOnInit() {
    const params = this.config.data;
    this.shipBin = params!.shipBin;

    this.addonGridOptions = AgFns.initGridOptions(this, {
      onGridReady: this.onAddonGridReady,
      onCellValueChanged: this.onCellValueChanged,
    });

    this.isPageReady = true;
  }

  onAddonGridReady = async (e: any) => {
    const colDefs = [
      {
        headerName: "J/O #",
        field: "joDetail.joHeader.id",
        cellClass: AgStyleFns.getLinkCellClass(),
        onCellClicked: (event: any) => {
          if (!event.value) return;
          const url = this.router.serializeUrl(
            this.router.createUrlTree(["/in-process/manage/job-orders"], NavFns.buildNavIdParams(event.value)),
          );
          window.open(url, "_blank");
        },
        ...AgFns.fixWidth(100),
      },
      { headerName: "Account", valueGetter: (p: any) => p.data?.joDetail?.joHeader?.account?.accountName, ...AgFns.flexWidth(100)},
      { headerName: "Manufacturer", valueGetter: (p: any) => p.data?.product?.productType?.manufacturer?.name, ...AgFns.flexWidth(100) },      
      { headerName: "Product", valueGetter: (p: any) => p.data?.product?.productType?.description, ...AgFns.flexWidth(150) },
      { headerName: "Features", field: "product.featureChoicesExtract", ...AgFns.flexWidth(150) },
      {
        headerName: "Process Bin",
        valueGetter: (p: any) => this.getAddonBin(p.data)?.id,
        cellClass: (p: any) => this.getAddonBin(p.data)?.id ? AgStyleFns.getLinkCellClass() : "",
        onCellClicked: (event: any) => {
          const binId = this.getAddonBin(event.data)?.id;
          if (!binId) return;
          const url = this.router.serializeUrl(
            this.router.createUrlTree(["/in-process/manage/addon-bin", binId]),
          );
          window.open(url, "_blank");
        },
        ...AgFns.fixWidth(150),
      },
      { headerName: "Current Station", valueGetter: (p: any) => this.getAddonBin(p.data)?.currentAddonStation?.name, ...AgFns.fixWidth(150) },
      { headerName: "Pending Station", valueGetter: (p: any) => this.getAddonBin(p.data)?.pendingAddonStation?.name, ...AgFns.fixWidth(170) },
      {
        headerName: "Priority",
        valueGetter: (p: any) => this.getAddonBin(p.data)?.priorityOrder,
        valueSetter: (p: any) => {
          const addonBin = this.getAddonBin(p.data);
          if (addonBin) {
            addonBin.priorityOrder = +p.newValue;
            return true;
          }
          return false;
        },
        editable: (p: any) => !!this.getAddonBin(p.data),
        ...AgFns.fixWidth(110),
      },
      { headerName: "Status", field: "itemDetailStatus.name", ...AgFns.fixWidth(240) },
    ];
    AgFns.initGrid(e, colDefs);

    await this.loadAddonItems();
  };

  async loadAddonItems() {
    this.addonItems = await this.dbQueryService.getAddonItemDetailsForShipBin(this.shipBin.id);
    AgFns.refreshGrid(this.addonGridOptions, this.addonItems);
  }

  get totalItemCount() {
    return this.addonItems.length;
  }

  onCellValueChanged = async (event: any) => {
    if (event.colDef.headerName === "Priority") {
      const addonBin = this.getAddonBin(event.data);
      if (addonBin) {
        try {
          await this.dbSaveService.saveSelectedChanges([addonBin]);
          this.toastr.success(`Priority updated to ${addonBin.priorityOrder}`);
          this.addonGridOptions.apiX?.refreshCells({ force: true });
        } catch (error) {
          this.toastr.error("Failed to save priority.");
          addonBin.entityAspect.rejectChanges();
          this.addonGridOptions.apiX?.refreshCells();
        }
      }
    }
  };

  getAddonBin(itd: ItemDetail) {
    const itda = itd?.itemDetailAddons?.find((a) => a.addonBinId != null);
    return itda?.addonBin;
  }

  onClose() {
    this.ref.close(null);
  }
}
